import { createApi } from '@reduxjs/toolkit/query/react';
import { IUserSchema } from '../../interfaces/user/IUserSchema';
import { ApiResponse } from '../../interfaces/models/models/paginated-response.model';
import { userAxiosQuery } from '../../utils/axios/user.axios';
import { IPaginationPayload } from '../../interfaces/shared/IPaginationPayload';
import { PaginatedUsers } from '../../interfaces/user/paginatedUser.type';
import { readFromStorage } from '../../utils/webStorage';
import { IChatResponse } from '../../interfaces/chats/IChatResponse';
import { CLAIRE_DASHBOARD_ID } from '../../config/env';

export const userAPI = createApi({
    reducerPath: 'users',
    baseQuery: userAxiosQuery(),
    tagTypes: ['auth', 'Posts'],
    endpoints: (builder) => ({
        getAllUsers: builder.query<PaginatedUsers, IPaginationPayload>({
            query: ({ filters, pageNumber, pageSize }: IPaginationPayload) => ({
                url: filters
                    ? `/users?page=${pageNumber}&size=${pageSize}&filters=${filters}`
                    : `/users?page=${pageNumber}&size=${pageSize}`,
                method: 'GET',
            }),
        }),
        setPassword: builder.mutation<PaginatedUsers, { username: string, password: string }>({
            query: ({ username, password }) => ({
                url: `/${username}/set-password`,
                method: 'PUT',
                data: { password }
            }),
        }),
        getCurrentUser: builder.query<{ data: any }, void>({
            query: () => ({
                url: '/rebecca-agent-dom.controller/authorize',
                method: 'GET',
            }),
            providesTags: ['auth']
        }),
        getSingleUser: builder.query<IUserSchema, string>({
            query: (id: string) => ({
                url: `/user/${id}`,
                method: 'GET',
            }),
        }),
        postUser: builder.mutation<ApiResponse<IUserSchema>, IUserSchema>({
            query: (data) => ({
                url: '/sign-up',
                method: 'POST',
                data,
            }),
        }),
        addToGroup: builder.mutation<IUserSchema, { username: string; group: string }>({
            query: ({ username, group }) => ({
                url: `/${username}/add-to-group`,
                method: 'POST',
                data: { group },
            }),
        }),
        removeFromGroup: builder.mutation<IUserSchema, { username: string; group: string }>({
            query: ({ username, group }) => ({
                url: `/${username}/remove-from-group`,
                method: 'POST',
                data: { group },
            }),
        }),
        userNavigation: builder.query<any, void>({
            query: () => ({
                url: '/users/access-control',
                method: 'GET',
                data: {
                    token: localStorage.getItem('accessToken') || ''
                },
            }),
        }),
        confirmUser: builder.mutation<IUserSchema, string>({
            query: (username) => ({
                url: `/${username}/confirm`,
                method: 'GET',
            }),
        }),
        userProfileUrl: builder.mutation<any, string>({
            query: (data) => ({
                url: `/rebecca-users/users/update-profile-picture`,
                method: 'PUT',
                data: data
            }),
        }),
        updateUserData: builder.mutation<any, string>({
            query: (data) => ({
                url: `/rebecca-users/users/update-profile`,
                method: 'PUT',
                data: data
            }),
        }),
        getWingUserHash: builder.mutation<any, void>({
            query: () => ({
                url: '/users/intercom',
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${readFromStorage('accessToken')}`,
                },
            }),
        }),
        newPassword: builder.mutation<any, any>({
            query: (data) => ({
                url: `/dom/admin/set-password`,
                method: 'PUT',
                data: data
            }),
        }),
        signUpWithAgent: builder.mutation<{
            data: {
                chatData: IChatResponse[],
                chatId?: string,
                metadata: { chatId: string, accessToken: string, refreshToken: string }
            },
        }, {
            /** Auth / legacy: full conversation turns */
            chatData?: IChatResponse[];
            usedBefore?: boolean;
            dashboard?: string;
            /** Exec by-admin (Rebecca parity): flat payload */
            actionSelected?: string;
            message?: string;
            files?: unknown[];
            topic?: string;
            stage?: string;
            chatId?: string;
        }>({
            query: (payload) => ({
                url: '/rebecca-users/sign-up/by-admin',
                method: 'PUT',
                data: {
                    ...payload,
                    dashboard: CLAIRE_DASHBOARD_ID,
                },
            })
        }),
    }),
});

export const {
    useGetAllUsersQuery,
    useSetPasswordMutation,
    useGetSingleUserQuery,
    usePostUserMutation,
    useAddToGroupMutation,
    useRemoveFromGroupMutation,
    useUserProfileUrlMutation,
    useUpdateUserDataMutation,
    useGetCurrentUserQuery,
    useConfirmUserMutation,
    useUserNavigationQuery,
    useGetWingUserHashMutation,
    useSignUpWithAgentMutation,
    useNewPasswordMutation
} = userAPI;
